import React, { useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import AuthCode from 'react-auth-code-input'

const PageBackground = styled.div`
  background: #04111b;
  min-height: 100dvh;
  width: 100dvw;
  padding-bottom: 300px;
  overflow: hidden;
`

export const MaxWidth = styled.div<{ maxWidth: number }>`
  max-width: ${({ maxWidth }) => maxWidth}px;
  margin: auto;
  z-index: 4;
  --max-width-padding-x: 4rem;
  @media screen and (max-width: ${({ maxWidth }) =>
      maxWidth + 64}px) and (min-width: 1000px) {
    padding: 0 64px;
    --max-width-padding-x: 64px;
  }
  @media screen and (max-width: 1000px) {
    padding: 0 16px;
    --max-width-padding-x: 16px;
  }
`

export const ForceFullWidth = styled.div<{ position?: string }>`
  width: 100vw;
  position: ${({ position }) => position ?? 'relative'};
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
`

export const Background = styled.div<{
  backgroundUrl: string
  mobileBackgroundUrl?: string
  height: string
  mobileH: string
  top?: number
}>`
  background-image: url('${({ backgroundUrl }) => backgroundUrl}');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  height: ${({ height }) => height};
  @media (max-width: 768px) {
    background-image: url('${({ backgroundUrl, mobileBackgroundUrl }) =>
      mobileBackgroundUrl ?? backgroundUrl}');
    width: calc(100vw + 32px); // 16px padding on each side
    left: -16px;
    height: ${({ mobileH }) => mobileH};
  }

  position: absolute;
  top: -${({ top }) => top ?? 0}px;
  left: 0;
  right: 0;
  overflow: hidden;
  width: 100vw;
  z-index: 1;

  @media (min-width: 1600px) {
    background-size: contain;
    ::before {
      content: '';
      position: absolute;
      left: 0;
      top: -${({ top }) => top ?? 0}px;
      overflow: hidden;
      width: 100%;
      // max-width: calc((100vw - 1400px) / 2);
      max-width: calc((100vw - 1400px));
      height: 100%;
      z-index: 1;
      background: linear-gradient(90deg, #04111b 50%, rgba(4, 17, 27, 0) 100%);
    }
    ::after {
      content: '';
      position: absolute;
      right: 0;
      top: -${({ top }) => top ?? 0}px;
      overflow: hidden;
      width: 100%;
      // max-width: calc((100vw - 1400px) / 2);
      max-width: calc((100vw - 1400px));
      height: 100%;
      z-index: 1;
      background: linear-gradient(-90deg, #04111b 50%, rgba(4, 17, 27, 0) 100%);
    }
  }
`

const Box = styled.div`
  padding-top: 40px;

  position: relative;
  z-index: 1;

  width: 100%;
  max-width: 743px;
  margin: 0 auto;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 57px;

  img {
    margin-bottom: 213px;
  }

  h1 {
    color: #fff;
    text-align: center;
    leading-trim: both;
    text-edge: cap;
    font-variant-numeric: ordinal;
    font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
    font-family: 'Space Grotesk';
    font-size: 78px;
    font-style: normal;
    font-weight: 700;
    line-height: 64px; /* 82.051% */
    letter-spacing: -1.56px;
  }

  p.text {
    color: #fff;
    text-align: center;
    font-family: 'Space Grotesk';
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  p.info {
    color: #d3d7e4;
    text-align: center;
    font-family: 'Space Grotesk';
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: 24px; /* 150% */
  }

  .input-box > div {
    display: flex;
    gap: 42px;
  }

  input {
    background: #04111b;
    color: #fff;
    text-align: center;
    font-family: 'Space Grotesk';
    font-size: 30px;
    font-style: normal;
    font-weight: 700;
    letter-spacing: -0.3px;
    text-transform: uppercase;
    width: 58px;
    height: 58px;
    line-height: 58px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    outline: none;
  }

  a {
    color: #d3d7e4;
    font-family: 'Space Grotesk';
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: 24px;
    text-decoration-line: underline;
  }

  @media screen and (max-width: 768px) {
    img {
      margin-bottom: 70px;
    }
    h1 {
      font-size: 48px;
      line-height: 1;
    }
    p.text {
      font-size: 18px;
    }

    .input-box > div {
      gap: 16px;
    }

    input {
      font-size: 24px;
      width: 48px;
      height: 48px;
    }
  }
`

const BoxInner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
`

const buttonStyles = css`
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.18px;
  text-transform: uppercase;

  display: inline-flex;
  flex: unset;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  padding: 0 27px;
  height: 44px;
  max-width: 240px;
  transition: 0.15s ease-in-out;

  color: black;
  background-color: #fdfd0f;
  border: 1px solid #fdfd0f;
  --button-border-color: #fdfd0f;
  :hover {
    background-color: #fdfd0f;
    filter: brightness(0.75);
    border: 1px solid #fdfd0f;
    --button-border-color: #fdfd0f;
    color: black;
  }
  :hover:enabled {
    cursor: pointer;
  }
  :disabled {
    opacity: 0.5;
  }
`

export const StyledThemedButton = styled.button`
  ${buttonStyles}
`

export const getIsValidInvideCode = () => {
  const realCode = '1337Z'
  const localCode = localStorage.getItem('testnet-invite-code') ?? ''
  return localCode.toLocaleLowerCase() === realCode.toLocaleLowerCase()
}

const InviteCodePage = () => {
  const realCode = '1337Z'
  const [code, setCode] = useState('')

  const onClick = useCallback(() => {
    window.location.replace('/')
    localStorage.setItem('testnet-invite-code', code)
  }, [code])

  return (
    <PageBackground>
      <MaxWidth maxWidth={1400}>
        <ForceFullWidth>
          <Background
            backgroundUrl="/invite-page-cover.png"
            height="546px"
            mobileH="300px"
          />
        </ForceFullWidth>
        <Box>
          <img src="/zap-alpha-logo.svg" alt="ZAP Alpha" width="123px" />
          <h1>Join Early Access</h1>
          <BoxInner>
            <p className="text">
              PLEASE ENTER YOUR INVITE CODE BELOW TO GAIN ACCESS TO THE TESTNET
              ENVIRONMENT
            </p>
            <div className="input-box">
              <AuthCode onChange={setCode} length={5} />
            </div>
            <StyledThemedButton
              onClick={onClick}
              disabled={code.toLowerCase() !== realCode.toLocaleLowerCase()}
            >
              Take me to the future
            </StyledThemedButton>
            <p className="info">
              Don't have a code? Get in touch on{' '}
              <a
                href="https://twitter.com/zaponchain"
                title="@zaponchain"
                target="_blank"
                rel="noreferrer"
              >
                X
              </a>{' '}
              or{' '}
              <a
                href="https://discord.com/invite/zaponblast"
                title="zaponblast"
                target="_blank"
                rel="noreferrer"
              >
                Discord
              </a>
            </p>
          </BoxInner>
        </Box>
      </MaxWidth>
    </PageBackground>
  )
}

export default InviteCodePage
